/**
 * Default Environment Configuration (Runtime Placeholder)
 * 
 * This file serves as a placeholder for runtime initialization.
 * Actual environment-specific values are set at runtime by RuntimeEnvironmentService
 * based on the hostname where the application is deployed.
 * 
 * This enables a single build artifact to work on dev, qa, stg, and production.
 * API endpoint paths (which don't change per environment) are defined below.
 * Environment-specific values (gateways, URLs, MSAL config) are set at runtime.
 */

// API endpoint constants
const VEHICLE_ENDPOINT = '/v1/portal/vehicle';
const ACCOUNT_ENDPOINT = '/v1/portal/account';
const SERVICE_DESK_URL = 'https://toyotaconnected.atlassian.net/servicedesk/customer/portal/26/group/250';

export const environment = {
  envName: 'dev',                    // Set at runtime
  userRoles: [
    "CTP.BEC.TSTReadOnly.US-STG",
    "CTP.Internal.TSTReadOnly.US-STG",
    "CTP.TSTAdmin-STG",
    "CTP.CallCenter.TSTReadOnly.MX-STG",
    "CTP.CallCenter.TSTReadOnly.PR-STG",
    "CTP.CallCenter.TSTReadOnly.HI-STG",
    "CTP.CallCenter.TSTReadOnly.CA-STG",
    "CTP.TSTRemoteAuth-STG",
    "CTP.VCMAdminPlus-STG",
    "CTP.VCMAdmin-STG"
  ],
  production: false,                 // Set at runtime
  svpp_gateway: '',                  // Set at runtime
  admin_guide_url: '',               // Set at runtime
  navi_portal_url: '',               // Set at runtime
  service_desk: SERVICE_DESK_URL,
  X_BRAND: 'T',
  adminportal_GW: '',                // Set at runtime
  admin: {
    apis: {
      customer_search: ACCOUNT_ENDPOINT,
      customer_update: ACCOUNT_ENDPOINT,
      email_swap: ACCOUNT_ENDPOINT,
      verify_code: '/v1/portal/account/code',
      delete_account: ACCOUNT_ENDPOINT,
      guid_status: '/v1/account/details',
      customer_history: '/v1/account/customer-history',
      dcmactivation: '/v1/provisioning/dcmactivation',
      survivor_account: '/v1/portal/rtt/survivors',
      plug_and_charge: '/v1/portal/ev/pnc/status',
      subscription: {
        details: VEHICLE_ENDPOINT,
        vehicleAssociation: '/v1/portal/vehicle/guid',
        vinlist: '/v1/portal/vehicle/data',
        cancelSubscription: '/subscription/v1/cancel',
        unLink: '/v1/portal/unlink-vehicle',
        preview: '/v5/subpreview',
        remote_legacy: '/v1/remote/legacy/command-history',
        remote: '/v1/global/remote/command-history',
        remote_gr86: '/v1/remote/ng86/command-history',
        remote_24mm: '/v1/portal/admin/remote/24mm/command-history',
        subscription_history: '/v1/subscription-management/history',
        dynamicNaviPart: '/v1/dynamicNaviPart',
        attWifiStatus: '/v2/wifistatus',
        verify_address: '/v1/address',
        getProvisioning: '/v1/portal/provisioning/status',
        emergencyContact: '/v1/portal/emergency-contact',
        zuora: '/v1/zuora/payments',
        retrigerProvisioningPackage: '/v2/provisioning/vehicleprovision',
        retrigerProvisioningRemote: '/v1/provisioning/dcmprovision',
        retrigerProvisioningService: '/v1/provisioning/dcmprovision',
        retrigerProvisioningHeadunit: '/v1/provisioning/huprovision',
        retrigerProvisioningSafety: '/v1/provisioning/safetyprovision',
        homeDealer: '/v1/homedealer',
        preferredDealer: '/v1/preferred-dealer',
        outageBanner: '/v1/banner',
        vehicleHistory: '/v1/portal/vehicle/history/',
        productPackages: '/products/v3/packages',
        trialLengths: '/v1/portal/trial-lengths',
        provision_history: '/v1/provisioning/provisioninghistory',
        linkedAccounts: '/v1/portal/linked-accounts',
        userProfileSettings: '/v1/portal/user-profile/settings',
        lookUpKey: '/v1/portal/lookupkeys',
        getGoodwillPackages: '/v1/goodwill',
        submitGoodwillPackages: '/v1/subscription/goodwill',
        getDataConsents: '/v1/portal/dataconsent',
        getAccountSmsConsent: '/v1/portal/consent/account',
        getLegacyDataConsents: '/v2/dataconsent',
        auto_authorize: '/authcode/v1/remote',
        auto_authorize_legacy: '/v1/legacy/vehicle/authorize',
        hucompany: '/v1/headunit-supplier',
        updateRemoteUser: '/v1/subscription/remoteguid',
        reSyncFlags: '/v6/vehicle',
        getVehicleAssociation: '/v3/vehicleassociation'
      },
      tss: '/v1/servicestatus',
      phoneComapatibilities: '/v1/bluetooth/compaitibility',
      jiraServiceDesk: {
        createTicket: '/v1/portal/admin/servicenow',
      }
    },
    cms: {
      requestTypeId: '170',
      groupId: '553'
    }
  },
  navi: {
    apis: {
      dynamicNaviPart: '/v1/dynamicNaviPart'
    }
  },
  ProductCatalog: {
    package_x_channel: 'UI',
    apis: {
      package: '/v2/products',
      packagev2: '/v3/products',
      asi: '/v2/dataingestion/asicapabilities',
      asiv2: '/v3/dataingestion/asicapabilities',
      getAsi: '/v1/asiCapabilities',
      getAsiV2: '/v2/asiCapabilities',
      getPackage: '/products/v1/products',
      generation: '/v1/uniqueGenerations',
      approveProduct: '/v1/products/approve',
      approveAsi: '/v1/asiCapabilities/status',
      approveAsiV2: '/v2/asicapabilities/status',
      user: '/v1/users',
      asiCapabilities: '/v1/capabilities/generation',
      productConfiguration: '/v1/productconfig'
    }
  },
  bulk: {
    goodwill: {
      apis: {
        postGoodwill: '/subscription/v1/goodwill'
      }
    }
  },
  fanda: {
    apis: {
      getfanda: '/v1/rejects',
      updateFanda: '/v1/corrections'
    }
  },
  us_config: {
    clientID: '',                      // Set at runtime
    tenantID: '',                      // Set at runtime
    policy: '1Toyota-MFA-CloudApps-External',
    authority: '',                    // Set at runtime
    redirectUri: '',                  // Set at runtime
    consentScopes: [
      'openid'
    ],
    queryParams: 'p=1Toyota-MFA-CloudApps-External'
  },
  reports: {
    apis: {
      tci: '/reports/tci',
      telematics: '/reports/telematics',
      sxm: '/reports/sxm',
    },
  },
  dealer_admin: {
    apis: {
      metrics: '/v1/dealers/admin/appointments/metrics',
      byMonth: '/v1/dealers/admin/appointments/metrics/monthly',
      byBrand: '/v1/dealers/admin/appointments/metrics/brand',
      byOS: '/v1/dealers/admin/appointments/metrics/os',
    },
  },
  user_roles: {
    apis: {
      get_roles: '/v1/users',
      update_roles: '/v1/user',
      list_role: '/v1/roles'
    },
  },
  vcm_admin: {
    apis: {
      details: VEHICLE_ENDPOINT,
      vcm_history_vin: '/v1/portal/vcm/history',
      vcm_clear_vin: '/v1/portal/vcm/removecert'
    },
  },
  rental_ev: {
    apis: {
      summary: '/v1/customer/fleetinfo'
    }
  }
};
